import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { VariantPickerWidgetProps, VariantPickerWidgetState } from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { themeColor } from "../../../../types/color";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: VariantPickerWidgetState = {
  // Card/Pill container
  borderColor: themeColor("borderColor"),
  borderColorSelected: themeColor("primary"),
  backgroundColor: themeColor("secondary"),
  backgroundColorHover: themeColor("background"),
  borderRadius: 8,

  // Rectangle card dimensions
  cardWidth: 88,
  cardGap: 12,
  cardImageSize: 72,

  // Compact pill dimensions
  pillImageSize: 32,
  pillGap: 8,
  pillPaddingY: 6,
  pillPaddingX: 12,

  // Checkmark badge
  checkmarkBgColor: themeColor("primary"),
  checkmarkIconColor: themeColor("secondary"),
  checkmarkSize: 16,

  // Price text
  priceColor: themeColor("textPrimary"),
  priceFontSize: 16,
  priceFontWeightNormal: "medium",
  priceFontWeightSelected: "semibold",
  priceFontFamily: "inherit",

  // Variant name text
  nameColor: themeColor("textPrimary"),
  nameFontSize: 14,
  nameFontSizeCompact: 14,
  nameFontWeightNormal: "medium",
  nameFontWeightSelected: "semibold",
  nameFontFamily: "inherit",

  // Divider
  dividerColor: themeColor("background"),
  dividerColorSelected: themeColor("primary"),
};

const editFields: EditField[] = [
  // Card/Pill container
  { key: "borderColor", label: "Border Color", type: "color" },
  {
    key: "borderColorSelected",
    label: "Border Color (Selected)",
    type: "color",
  },
  { key: "backgroundColor", label: "Background Color", type: "color" },
  {
    key: "backgroundColorHover",
    label: "Background Color (Hover)",
    type: "color",
  },
  {
    key: "borderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 16,
    step: 2,
  },

  // Rectangle card dimensions
  {
    key: "cardWidth",
    label: "Card Width",
    type: "number",
    min: 60,
    max: 120,
    step: 4,
  },
  {
    key: "cardGap",
    label: "Card Gap",
    type: "number",
    min: 4,
    max: 24,
    step: 2,
  },
  {
    key: "cardImageSize",
    label: "Card Image Size",
    type: "number",
    min: 48,
    max: 96,
    step: 4,
  },

  // Compact pill dimensions
  {
    key: "pillImageSize",
    label: "Pill Image Size",
    type: "number",
    min: 24,
    max: 48,
    step: 4,
  },
  {
    key: "pillGap",
    label: "Pill Gap",
    type: "number",
    min: 4,
    max: 16,
    step: 2,
  },
  {
    key: "pillPaddingY",
    label: "Pill Padding Y",
    type: "number",
    min: 4,
    max: 16,
    step: 2,
  },
  {
    key: "pillPaddingX",
    label: "Pill Padding X",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
  },

  // Checkmark badge
  { key: "checkmarkBgColor", label: "Checkmark Background", type: "color" },
  { key: "checkmarkIconColor", label: "Checkmark Icon Color", type: "color" },
  {
    key: "checkmarkSize",
    label: "Checkmark Size",
    type: "number",
    min: 12,
    max: 24,
    step: 2,
  },

  // Price text
  { key: "priceColor", label: "Price Color", type: "color" },
  {
    key: "priceFontSize",
    label: "Price Font Size",
    type: "number",
    min: 12,
    max: 20,
    step: 1,
  },
  {
    key: "priceFontWeightNormal",
    label: "Price Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "priceFontWeightSelected",
    label: "Price Weight (Selected)",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "priceFontFamily",
    label: "Price Font Family",
    type: "font",
  },

  // Variant name text
  { key: "nameColor", label: "Name Color", type: "color" },
  {
    key: "nameFontSize",
    label: "Name Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
  },
  {
    key: "nameFontSizeCompact",
    label: "Name Font Size (Compact)",
    type: "number",
    min: 10,
    max: 16,
    step: 1,
  },
  {
    key: "nameFontWeightNormal",
    label: "Name Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "nameFontWeightSelected",
    label: "Name Weight (Selected)",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "nameFontFamily",
    label: "Name Font Family",
    type: "font",
  },

  // Divider
  { key: "dividerColor", label: "Divider Color", type: "color" },
  {
    key: "dividerColorSelected",
    label: "Divider Color (Selected)",
    type: "color",
  },
];

export const VariantPickerWidget: React.FC<VariantPickerWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<VariantPickerWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve font families - if "inherit", use inherit; otherwise look up from themeFonts
  const priceFontKey = state.priceFontFamily;
  const priceFontData =
    priceFontKey && priceFontKey !== "inherit"
      ? themeFonts[priceFontKey]
      : null;

  const nameFontKey = state.nameFontFamily;
  const nameFontData =
    nameFontKey && nameFontKey !== "inherit" ? themeFonts[nameFontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  const priceGoogleFontLink =
    priceFontData && priceFontData.format === "google"
      ? priceFontData.url
      : null;
  const nameGoogleFontLink =
    nameFontData && nameFontData.format === "google" ? nameFontData.url : null;

  // Load Google Fonts into document.head when fonts change
  useEffect(() => {
    if (priceGoogleFontLink) {
      loadGoogleFontInHead(priceGoogleFontLink);
    }
  }, [priceGoogleFontLink]);

  useEffect(() => {
    if (nameGoogleFontLink) {
      loadGoogleFontInHead(nameGoogleFontLink);
    }
  }, [nameGoogleFontLink]);

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  const priceFontFamilyCss = priceFontData
    ? priceFontData.format === "system"
      ? priceFontData.url
      : `"${priceFontData.name}", sans-serif`
    : "inherit";

  const nameFontFamilyCss = nameFontData
    ? nameFontData.format === "system"
      ? nameFontData.url
      : `"${nameFontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts)
  const priceNeedsFontFace =
    priceFontData &&
    priceFontData.format !== "system" &&
    priceFontData.format !== "google";
  const nameNeedsFontFace =
    nameFontData &&
    nameFontData.format !== "system" &&
    nameFontData.format !== "google";

  // Generate unique class for this widget instance
  const uniqueClass = `variant-picker-${widgetId || "default"}`;

  // Generate @font-face CSS and font-family rules
  const styleCss = `
    ${
      priceNeedsFontFace
        ? `
      @font-face {
        font-family: "${priceFontData.name}";
        src: url("${priceFontData.url}") format("${priceFontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      nameNeedsFontFace
        ? `
      @font-face {
        font-family: "${nameFontData.name}";
        src: url("${nameFontData.url}") format("${nameFontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      priceFontFamilyCss !== "inherit"
        ? `.${uniqueClass} [data-price-text] { font-family: ${priceFontFamilyCss} !important; }`
        : ""
    }
    ${
      nameFontFamilyCss !== "inherit"
        ? `.${uniqueClass} [data-name-text] { font-family: ${nameFontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Create enhanced state with resolved fontFamily CSS
  const enhancedState: VariantPickerWidgetState & {
    priceFontFamilyCss?: string;
    nameFontFamilyCss?: string;
  } = {
    ...state,
    priceFontFamilyCss,
    nameFontFamilyCss,
  };

  // Render the children with current state and font styles
  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      {children(enhancedState, resolveColor)}
    </>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
